// The sliding highlight — the single most repeated behaviour in the redesign.
//
// One indicator element per group, measured from the DOM and moved with a
// transform. It drives the top nav, the settings rail, every segmented control
// and every filter bar; horizontal and vertical alike, because it writes both
// offsets and both sizes and lets the group's own layout decide which of them
// actually change.
//
// Plain DOM rather than React so that the one caller that isn't React — the
// top nav, which is ERB and has to survive a Turbo body swap — can share the
// same code as components/ui/TabGroup.tsx rather than reimplementing it. This
// module is the implementation; both are thin adapters over it.
//
// The contract, per 00-foundation.md §5.1:
//   - the group is position:relative and carries [data-tabs]
//   - its first child is the indicator, [data-tab-ind], absolutely positioned
//     at 0,0 with box-sizing:border-box, opacity 0 and pointer-events:none
//   - each option carries [data-tab-on="1"|"0"] and position:relative, and
//     paints no background of its own

/** The transition every indicator but the top nav's runs. */
export const TAB_INDICATOR_TRANSITION =
  'transform .34s cubic-bezier(.22,.92,.24,1),width .34s cubic-bezier(.22,.92,.24,1),height .34s cubic-bezier(.22,.92,.24,1),opacity .18s linear'

/**
 * The top nav's, which is the same curve a touch slower: it travels further
 * than any other group in the product, and at .34s a jump from Overview to
 * Studio reads as a flick rather than a slide.
 */
export const NAV_INDICATOR_TRANSITION =
  'transform .38s cubic-bezier(.22,.92,.24,1),width .38s cubic-bezier(.22,.92,.24,1),opacity .2s linear'

/**
 * Place one group's indicator over whichever of its options is currently on.
 *
 * `transition` is what gets restored after the very first placement. Until
 * then the indicator is moved with no transition at all and a reflow forced in
 * between, so it fades in where it belongs instead of flying in from the
 * group's top-left corner — which is what it would otherwise do on every mount,
 * every Turbo render and every tab-panel switch.
 */
export function placeTabIndicator(group: HTMLElement, transition = TAB_INDICATOR_TRANSITION) {
  const indicator = group.querySelector<HTMLElement>('[data-tab-ind]')
  if (!indicator) return

  const active = group.querySelector<HTMLElement>('[data-tab-on="1"]')
  if (!active) {
    // Nothing selected — a filter bar with the "All" chip cleared, say. The
    // indicator keeps its last geometry so that reselecting slides from where
    // the eye last saw it rather than from the corner.
    indicator.style.opacity = '0'
    return
  }

  // offsetLeft/offsetTop rather than getBoundingClientRect: the indicator is
  // positioned against the group, the options are laid out inside it, and the
  // group is the offsetParent for both — so these are already in the same
  // coordinate space the transform is written in, with no origin to subtract
  // and no dependency on either box's current transform.
  const first = indicator.style.opacity !== '1'
  if (first) indicator.style.transition = 'none'

  indicator.style.width = `${active.offsetWidth}px`
  indicator.style.height = `${active.offsetHeight}px`
  indicator.style.transform = `translate(${active.offsetLeft}px,${active.offsetTop}px)`
  indicator.style.opacity = '1'

  if (first) {
    void indicator.offsetWidth
    indicator.style.transition = transition
  }
}

/** Every group under `root`, for the resize and font-load passes. */
export function placeAllTabIndicators(root: ParentNode = document) {
  root.querySelectorAll<HTMLElement>('[data-tabs]').forEach((group) => placeTabIndicator(group))
}

/**
 * Re-place on the two events that change an option's measured box without
 * changing which one is active: a resize, and the brand/body faces landing
 * after first paint (they are wider than the fallbacks, so an indicator placed
 * before they arrive sits under where the label *was*).
 *
 * Returns a teardown, so a React caller can hand it straight back from an
 * effect.
 */
export function watchTabIndicators(place: () => void): () => void {
  const onResize = () => place()
  window.addEventListener('resize', onResize)
  document.fonts?.ready.then(place).catch(() => {})
  return () => window.removeEventListener('resize', onResize)
}
